import { useState, useRef, useEffect } from 'react';
import { INVEST_MASKS } from './accounts';
import {
  maskByAccountId, summarizeAssets, holdingsTotals, snapshots, netWorthOf, inclBusinessOf, netWorthSeries,
  priorChange, scorecardWindows, marketDayWindow, priceDateOn, marketStatus, SYNC_MINUTES_CT, netWorthChange, marketChange, holdingsCheck, allocation, holdingsTable, sweepTickers, activity, BUSINESS_MASK,
} from './netWorth';
import PerformanceSection from './PerformanceSection';
import { fmtBal, fmtCents, fmtDay, fmtDayShort, fmtWeekdayShort, fmtClockCT, fmtMinutes } from './format';

// The balance-sheet tab. Everything here is read-only: Finance.js loads the
// lists and passes the rows in, netWorth.js does the arithmetic, and this file
// only draws it.

const mask = m => (m ? `····${m}` : '—');

// Signed, to the cent: "+$1,486.12" / "−$16.72".
const fmtSigned = n => (n > 0 ? '+' : '') + fmtCents(n);
// Signed, to the dollar: "+$1,486" / "−$17" / "$0".
const fmtSignedBal = n => (Math.round(n) > 0 ? '+' : '') + fmtBal(n);
const fmtPct    = n => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n * 100).toFixed(1)}%`;
const gainColor = n => (n > 0 ? '#2e7d32' : n < 0 ? '#c62828' : '#888');

// Allocation colours, fixed per bucket (never by rank) and checked as a set for
// colour-blind separation between neighbours on a white card.
const BUCKET_COLOR = {
  cash: '#2e7d32', retirement: '#6a35b8', brokerage: '#0d9488',
  metals: '#B8860B', vehicles: '#2a78d6', other: '#d55181',
};

const KIND_LABEL = {
  reinvest: 'Reinvested', buy: 'Buy', sell: 'Sell', dividend: 'Dividend', interest: 'Interest',
  fee: 'Fee', transfer: 'Transfer', cash: 'Cash', other: 'Other',
};

// ── Line chart ────────────────────────────────────────────────────────────────

const CH_H = 190, CH_PAD = { l: 58, r: 66, t: 16, b: 24 };

// Round axis steps: 1, 2, 2.5 or 5 times a power of ten.
function niceStep(raw) {
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  return [1, 2, 2.5, 5, 10].map(m => m * pow).find(s => s >= raw);
}

// Every stated day has a tooltip — date, household net worth, change against
// the prior stated point — reached three ways: hover, a tap (which pins it
// until a tap anywhere else), and keyboard focus on the point.
// `live`: the last point is today's and can still move — 'live' (market open,
// green) or 'settling' (after the bell, amber). It carries a slow pulse (still,
// for reduced motion) until the day's number is final.
const LIVE_COLOR = { live: '#2e7d32', settling: '#c77700' };
function NetWorthChart({ series, live = null }) {
  const wrap = useRef(null);
  const [width, setWidth] = useState(660);
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(false);

  // The chart fills its column, so the drawing is redone at the real width
  // instead of being scaled (which would shrink the text on a phone).
  useEffect(() => {
    const measure = () => { if (wrap.current?.clientWidth) setWidth(wrap.current.clientWidth); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // A pinned tooltip lets go on a tap anywhere outside the chart.
  useEffect(() => {
    if (!pinned) return undefined;
    const away = e => { if (!wrap.current?.contains(e.target)) { setPinned(false); setHover(null); } };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [pinned]);

  const stated = series.filter(p => p.value !== null);
  if (!stated.length) return <p className="fin-empty">No complete daily snapshot to plot yet.</p>;

  let lo = Math.min(...stated.map(p => p.value)), hi = Math.max(...stated.map(p => p.value));
  if (hi === lo) { const pad = Math.abs(hi) * 0.05 || 1; lo -= pad; hi += pad; }
  const step = niceStep((hi - lo) / 3);
  lo = Math.floor(lo / step) * step;
  hi = Math.ceil(hi / step) * step;
  const ticks = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(v);

  const plotW = width - CH_PAD.l - CH_PAD.r, plotH = CH_H - CH_PAD.t - CH_PAD.b;
  const x = i => CH_PAD.l + (series.length > 1 ? (i / (series.length - 1)) * plotW : plotW / 2);
  const y = v => CH_PAD.t + plotH - ((v - lo) / (hi - lo)) * plotH;

  // Unbroken runs of stated days. A null day ends the run: the line stops and
  // starts again on the far side, with nothing drawn across the gap.
  const runs = [];
  let run = [];
  series.forEach((p, i) => {
    if (p.value !== null) run.push(i);
    else if (run.length) { runs.push(run); run = []; }
  });
  if (run.length) runs.push(run);

  const lastIdx = series.lastIndexOf(stated[stated.length - 1]);
  const showDots = stated.length <= 31;

  const onMove = e => {
    if (pinned) return;
    const box = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - box.left;
    let best = null;
    series.forEach((p, i) => {
      if (p.value !== null && (best === null || Math.abs(x(i) - px) < Math.abs(x(best) - px))) best = i;
    });
    setHover(best);
  };
  const onLeave = () => { if (!pinned) setHover(null); };
  // A tap on a point pins its tooltip (a second tap on the same point lets go).
  const onTap = (i) => (e) => {
    e.stopPropagation();
    if (pinned && hover === i) { setPinned(false); setHover(null); return; }
    setHover(i);
    setPinned(true);
  };
  const tip = hover !== null ? series[hover] : null;
  const tipLeft = tip ? Math.min(Math.max(x(hover), 90), width - 90) : 0;
  const change = tip ? priorChange(series, hover) : null;
  const label = i => {
    const c = priorChange(series, i);
    return `${fmtDay(series[i].date)}: ${fmtBal(series[i].value)}` + (c ? `, ${fmtSignedBal(c.delta)} vs ${fmtDayShort(c.from)}` : ', first snapshot');
  };

  return (
    <div className="fin-nw-chart" ref={wrap}>
      <svg width={width} height={CH_H} role="img" onPointerMove={onMove} onPointerLeave={onLeave}
        aria-label={`Net worth per day, ${fmtDay(series[0].date)} to ${fmtDay(series[series.length - 1].date)}`}>
        {ticks.map(v => (
          <g key={v}>
            <line x1={CH_PAD.l} x2={width - CH_PAD.r} y1={y(v)} y2={y(v)} stroke="#ece6dc" strokeWidth="1" />
            <text x={CH_PAD.l - 8} y={y(v) + 3.5} textAnchor="end" fontSize="10" fill="#7a6a5a">{fmtBal(v)}</text>
          </g>
        ))}
        {runs.filter(r => r.length > 1).map(r => (
          <polyline key={r[0]} fill="none" stroke="#4B0082" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
            points={r.map(i => `${x(i)},${y(series[i].value)}`).join(' ')} />
        ))}
        {live && <circle className="fin-nw-pulse" cx={x(lastIdx)} cy={y(series[lastIdx].value)} r="4" fill={LIVE_COLOR[live]} />}
        {series.map((p, i) => {
          const lone = runs.some(r => r.length === 1 && r[0] === i);
          if (p.value === null || !(showDots || lone || i === lastIdx)) return null;
          return <circle key={p.date} className="fin-nw-dot" cx={x(i)} cy={y(p.value)} r={i === lastIdx ? 4 : 3}
            fill={live && i === lastIdx ? LIVE_COLOR[live] : '#4B0082'} stroke="#fff" strokeWidth="2" />;
        })}
        <text x={x(lastIdx) + 9} y={y(series[lastIdx].value) + 4} fontSize="11" fontWeight="600" fill="#2a1a0a">
          {fmtBal(series[lastIdx].value)}
        </text>
        {series.length > 1 ? (
          <>
            <text x={CH_PAD.l} y={CH_H - 6} fontSize="10" fill="#7a6a5a">{fmtDayShort(series[0].date)}</text>
            <text x={width - CH_PAD.r} y={CH_H - 6} textAnchor="end" fontSize="10" fill="#7a6a5a">{fmtDayShort(series[series.length - 1].date)}</text>
          </>
        ) : (
          <text x={x(0)} y={CH_H - 6} textAnchor="middle" fontSize="10" fill="#7a6a5a">{fmtDayShort(series[0].date)}</text>
        )}
        {tip && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={CH_PAD.t} y2={CH_PAD.t + plotH} stroke="#c4b89a" strokeWidth="1" />
            <circle cx={x(hover)} cy={y(tip.value)} r="5" fill="#4B0082" stroke="#fff" strokeWidth="2" />
          </g>
        )}
        {/* Hit targets: one per stated day, invisible, big enough for a finger, in the tab order. */}
        {series.map((p, i) => p.value === null ? null : (
          <circle key={`hit-${p.date}`} className="fin-nw-hit" cx={x(i)} cy={y(p.value)} r="11" fill="transparent"
            tabIndex={0} role="img" aria-label={label(i)}
            onClick={onTap(i)} onFocus={() => setHover(i)} onBlur={() => { if (!pinned) setHover(null); }} />
        ))}
      </svg>
      {tip && (
        <div className="fin-nw-tip" style={{ left: tipLeft }}>
          <strong>{fmtBal(tip.value)}</strong> {fmtDay(tip.date)}
          <div className="fin-nw-tip-change">
            {change
              ? <><span style={{ color: change.delta > 0 ? '#9be3a7' : change.delta < 0 ? '#ffb3ad' : '#ccc', fontWeight: 700 }}>{fmtSignedBal(change.delta)}</span> vs {fmtDayShort(change.from)}</>
              : 'first snapshot'}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Scorecard ─────────────────────────────────────────────────────────────────
// Day · Month · Year · Since start. Two lenses: Net worth (the household figure
// moving) and Market (what the investments earned, contributions set aside).
// The lens is remembered on this device; storage that is missing or refuses
// simply means Net worth every time.
const LENS_KEY = 'dh_netWorthLens';
function readLens() {
  try { return localStorage.getItem(LENS_KEY) === 'market' ? 'market' : 'networth'; } catch { return 'networth'; }
}
function writeLens(lens) {
  try { localStorage.setItem(LENS_KEY, lens); } catch { /* not remembered */ }
}

// "vs Sep 29" / "since Oct 1"; the year is printed once it differs from the latest snapshot's.
// The Market Day card names the day its figure belongs to instead: "Thu Oct 15",
// or "Today so far" while today's number is still live or settling.
const whenLabel = (w) => (w.soFar ? w.soFar : w.closeDate ? fmtWeekdayShort(w.closeDate)
  : w.from ? `${w.since ? 'since' : 'vs'} ${w.from.date.slice(0, 4) === w.to.date.slice(0, 4) ? fmtDayShort(w.from.date) : fmtDay(w.from.date)}` : '');

function Scorecard({ windows, lens, assetInfo, invTxns, maskById }) {
  return (
    <div className="fin-nw-score">
      {windows.map(w => {
        const nw = lens === 'networth' ? netWorthChange(w.from, w.to, assetInfo.net) : null;
        const mk = lens === 'market' ? marketChange(w.from, w.to, assetInfo, invTxns, maskById) : null;
        const amount = nw ? nw.delta : mk ? mk.gain : null;
        const pct = nw ? nw.pct : mk ? mk.pct : null;
        return (
          <div key={w.key} className="fin-card fin-nw-score-card">
            <div className="fin-card-label">{w.title}</div>
            {amount === null ? (
              <div className="fin-nw-score-value fin-nw-dash">—</div>
            ) : (
              <>
                <div className="fin-nw-score-value" style={{ color: gainColor(Math.round(amount)) }}>{fmtSignedBal(amount)}</div>
                <div className="fin-nw-score-pct" style={{ color: gainColor(Math.round(amount)) }}>{pct !== null ? fmtPct(pct) : '—'}</div>
                <div className="fin-nw-score-when">{whenLabel(w)}</div>
                <div className="fin-nw-score-sub">
                  {nw && <>investments {fmtSignedBal(nw.investments)} · cash {fmtSignedBal(nw.cash)}</>}
                  {mk && (mk.flows > 0 ? <>contributions {fmtSignedBal(mk.netIn)} excluded</> : 'no contributions')}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Freshness ─────────────────────────────────────────────────────────────────
// The page's clock. It ticks once a minute so LIVE turns into MARKET CLOSE on
// an open page; a fixed `now` (tests) never ticks.
function useNow(fixed) {
  const [now, setNow] = useState(() => fixed || new Date());
  useEffect(() => {
    if (fixed) return undefined;
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, [fixed]);
  return fixed || now;
}

// One line under "as of": a dot and whether today's number is done moving.
//   ● LIVE · updated 2:30 PM          green, pulsing — market open
//   ● SETTLING · final at 8:30 PM     amber, pulsing — after the bell, mutual funds still pricing
//   ● FINAL · Wed Nov 4               indigo, solid  — locked
// Shown only when the balance rows say when they were written.
const LAST_SYNC = SYNC_MINUTES_CT[SYNC_MINUTES_CT.length - 1];
function StatusLine({ status, date }) {
  if (!status?.updated) return null;
  const { state } = status;
  const detail = state === 'live' ? `updated ${fmtClockCT(status.updated)}`
    // next has wrapped to tomorrow's first run: 8:30 PM has passed and its update has not landed.
    : state === 'settling' ? (status.next === SYNC_MINUTES_CT[0] ? `${fmtMinutes(LAST_SYNC)} update pending` : `final at ${fmtMinutes(LAST_SYNC)}`)
    : fmtWeekdayShort(status.valueDate || date);
  return (
    <div className={`fin-nw-status fin-nw-status--${state}`}>
      <span className="fin-nw-status-dot" aria-hidden="true" />
      <span className="fin-nw-status-word">{state.toUpperCase()}</span>
      <span> · {detail}</span>
    </div>
  );
}

// ── Tab ───────────────────────────────────────────────────────────────────────

export default function NetWorthTab({ balances, accounts, assets, holdings, invTxns, now: fixedNow }) {
  const [acctFilter, setAcctFilter] = useState('All');
  const [showAll, setShowAll] = useState(false);
  const [lens, setLens] = useState(readLens);
  const chooseLens = (l) => { setLens(l); writeLens(l); };
  const now = useNow(fixedNow);

  const maskById  = maskByAccountId(accounts);
  const assetInfo = summarizeAssets(assets);
  const heldByDay = holdingsTotals(holdings, maskById);
  const snaps     = snapshots(balances, heldByDay);
  const latest    = snaps[snaps.length - 1] || null;
  const series    = netWorthSeries(snaps, assetInfo.net);
  const netWorth  = netWorthOf(latest, assetInfo.net);          // household — FIG and Trust are not in it
  const inclBiz   = inclBusinessOf(latest, assetInfo.net);
  const windows   = scorecardWindows(snaps);
  // Market lens: the Day card measures the last real move, labelled by the
  // day that figure belongs to. The balances decide that (marketStatus): the
  // holdings' price date while the two agree, the balance's own trading day
  // once it has moved past the holdings (Plaid reprices holdings overnight),
  // the walk-back's close day when nothing is dated. Every other card is the
  // same window in both lenses.
  const dayTo     = windows[0].to;
  const dayMove   = marketDayWindow(windows[0], snaps);
  const priceDate = dayTo ? priceDateOn(holdings, dayTo.date) : null;
  const status    = marketStatus({
    to: latest, held: latest ? heldByDay[latest.date] : null,
    priceDate: latest ? priceDateOn(holdings, latest.date) : null,
    walkClose: dayTo === latest ? dayMove.close || null : null, now,
  });
  const mktWindows = windows.map(w => {
    if (w.key !== 'day') return w;
    const m = dayMove;
    const closeDate = (dayTo === latest ? status?.valueDate : null) || priceDate || m.close || null;
    if (!closeDate || !m.from) return m;
    // Live or settling, and the figure already carries some of today's move.
    return dayTo === latest && status.moving ? { ...m, closeDate, soFar: 'Today so far' } : { ...m, closeDate };
  });
  const mktDay = mktWindows[0];
  const check     = holdingsCheck(latest, heldByDay);
  const mismatch  = !!check && check.off.length > 0 && !status?.lagging;
  // Today's chart point pulses while the number is live or settling.
  const livePoint = status?.updated && status.state !== 'final' && latest?.complete ? status.state : null;
  const alloc     = latest?.complete ? allocation(latest, assetInfo) : null;
  const table     = holdingsTable(holdings, maskById);
  const acts      = activity(invTxns, maskById, sweepTickers(holdings), { showAll });

  const heldRows   = table ? table.rows.filter(r => acctFilter === 'All' || r.mask === acctFilter) : [];
  const heldMasks  = table ? INVEST_MASKS.filter(m => table.rows.some(r => r.mask === m)) : [];
  const heldValue  = heldRows.reduce((s, r) => s + r.value, 0);
  const heldBasis  = heldRows.reduce((s, r) => s + (r.costBasis ?? 0), 0);
  const heldGain   = heldRows.reduce((s, r) => s + (r.gain ?? 0), 0);
  const heldWeight = heldRows.reduce((s, r) => s + r.weight, 0);

  const pill = (active) => ({
    padding: '0.18rem 0.55rem', border: '1px solid #c4b89a', borderRadius: 4, cursor: 'pointer',
    background: active ? '#4B0082' : '#faf7ee', color: active ? '#fff' : '#5a4a3a',
    fontWeight: 600, fontSize: '0.7rem',
  });

  return (
    <div className="fin-nw">

      {/* ── Headline ── */}
      <div className="fin-card fin-card--net fin-nw-head">
        <div className="fin-card-label">Net worth</div>
        {!latest ? (
          <div className="fin-nw-value">—</div>
        ) : (
          <>
            <div className="fin-nw-value">{netWorth !== null ? fmtBal(netWorth) : '—'}</div>
            <div className="fin-nw-caption">Household · excludes FIG operating cash and Trust</div>
            <div className="fin-nw-asof">as of {fmtDay(latest.date)}</div>
            <StatusLine status={status} date={latest.date} />
            {/* Manual accounts tagged Retirement/Brokerage count as investments here, not assets. */}
            <div className="fin-nw-parts">
              cash {fmtBal(latest.cash)} · investments {fmtBal(latest.investments + assetInfo.investmentTotal)} · assets {fmtBal(assetInfo.assetTotal - assetInfo.investmentTotal)}
              {assetInfo.liabilityTotal > 0 && <> · liabilities {fmtBal(-assetInfo.liabilityTotal)}</>}
            </div>
          </>
        )}
        <div className="fin-nw-notes">
          {!latest && <div>No balances recorded yet.</div>}
          {latest && !latest.complete && (
            <div className="fin-nw-note--warn">No balance for {latest.missing.map(mask).join(', ')} on this date — net worth can’t be stated.</div>
          )}
          {latest?.fallback.length > 0 && <div>{latest.fallback.map(mask).join(', ')} taken from holdings (no balance row that day).</div>}
          {/* The holdings cross-check speaks only when it has something to say: holdings
              priced at the balance's own day that still disagree. A gap against holdings
              priced at an earlier close is the market's move (Plaid reprices overnight). */}
          {mismatch && (
            <>
              <div>
                Holdings total {fmtBal(check.total)}
                {assetInfo.investmentTotal > 0 && <> · manual accounts {fmtBal(assetInfo.investmentTotal)}</>}
              </div>
              <div className="fin-nw-note--warn">holdings/balance mismatch — {check.off.map(mask).join(', ')}</div>
            </>
          )}
          {assetInfo.unvalued.length > 0 && (
            <div>{assetInfo.unvalued.length} {assetInfo.unvalued.length === 1 ? 'asset' : 'assets'} not yet valued</div>
          )}
        </div>
      </div>

      {/* ── Scorecard: how the household figure (or the investments) moved ── */}
      {latest && (
        <div className="fin-nw-lens">
          <div className="fin-nw-lens-toggle" role="group" aria-label="Scorecard lens">
            <button style={pill(lens === 'networth')} aria-pressed={lens === 'networth'} onClick={() => chooseLens('networth')}>Net worth</button>
            <button style={pill(lens === 'market')}   aria-pressed={lens === 'market'}   onClick={() => chooseLens('market')}>Market</button>
          </div>
          <Scorecard windows={lens === 'market' ? mktWindows : windows} lens={lens} assetInfo={assetInfo} invTxns={invTxns} maskById={maskById} />
          {/* When the figures were last written. Which day they belong to is on the status line and the Day card. */}
          {lens === 'market' && mktDay.to?.updated && (
            <p className="fin-nw-foot fin-nw-fresh">updated {fmtClockCT(mktDay.to.updated)}</p>
          )}
          <p className="fin-nw-foot">
            {lens === 'market'
              ? 'Investments only: the % covers the Chase investment accounts 6181 · 8223 · 9454, the ones priced daily. Contributions and withdrawals are set aside; dividends and interest count. Manual accounts are shown at current value and not in the %.'
              : 'Household net worth, FIG and Trust excluded. Manual assets are held at their current value.'}
          </p>
        </div>
      )}

      {/* ── Business, below the line: beside the household figure, never in it ── */}
      {latest && (
        <div className="fin-nw-biz">
          <div className="fin-nw-biz-row">
            <span>FIG operating cash</span>
            <span className="fin-nw-biz-value">{latest.business !== null ? fmtBal(latest.business) : '—'}</span>
          </div>
          <div className="fin-nw-biz-row">
            <span>Incl. business</span>
            <span className="fin-nw-biz-value">{inclBiz !== null ? fmtBal(inclBiz) : '—'}</span>
          </div>
          {latest.business === null && (
            <div className="fin-nw-biz-note">No balance for {mask(BUSINESS_MASK)} on this date.</div>
          )}
        </div>
      )}

      {/* ── Net worth over time ── */}
      <div className="fin-section">
        <h3 className="fin-section-title">Net Worth Over Time</h3>
        {series.length === 0 ? <p className="fin-empty">No data.</p> : <NetWorthChart series={series} live={livePoint} />}
        <p className="fin-nw-foot">
          Manual assets shown at current value for all dates.
          {livePoint && <> Today’s point is still moving; it locks at the 8:30 PM update.</>}
        </p>
      </div>

      {/* ── Us against the market, and week by week ── */}
      <PerformanceSection snaps={snaps} invTxns={invTxns} maskById={maskById} holdings={holdings} manualInvestments={assetInfo.investmentTotal} />

      {/* ── Allocation ── */}
      <div className="fin-section">
        <h3 className="fin-section-title">Allocation</h3>
        {!alloc || alloc.total <= 0 ? (
          <p className="fin-empty">No complete snapshot to allocate.</p>
        ) : (
          <>
            <div className="fin-nw-bar" role="img" aria-label="Allocation of assets by bucket">
              {alloc.buckets.filter(b => b.share > 0).map(b => (
                <div key={b.key} className="fin-nw-seg" title={`${b.label} ${fmtBal(b.value)} (${(b.share * 100).toFixed(1)}%)`}
                  style={{ flexGrow: b.share, background: BUCKET_COLOR[b.key] }} />
              ))}
            </div>
            <div className="fin-nw-legend">
              {alloc.buckets.filter(b => b.share > 0).map(b => (
                <div key={b.key} className="fin-nw-legend-item">
                  <span className="fin-nw-swatch" style={{ background: BUCKET_COLOR[b.key] }} />
                  <span className="fin-nw-legend-label">{b.label}</span>
                  <span className="fin-nw-legend-value">{fmtBal(b.value)}</span>
                  <span className="fin-nw-legend-pct">{(b.share * 100).toFixed(1)}%</span>
                </div>
              ))}
            </div>
            {assetInfo.liabilityTotal > 0 && (
              <div className="fin-nw-liab">Liabilities {fmtBal(-assetInfo.liabilityTotal)} — subtracted from net worth, not shown in the bar.</div>
            )}
          </>
        )}
      </div>

      {/* ── Holdings ── */}
      <div className="fin-section">
        <div className="fin-nw-titlebar">
          <h3 className="fin-section-title">Holdings{table ? ` — ${fmtDay(table.date)}` : ''}</h3>
          {table && ['All', ...heldMasks].map(m => (
            <button key={m} style={pill(acctFilter === m)} onClick={() => setAcctFilter(m)}>{m === 'All' ? 'All accounts' : mask(m)}</button>
          ))}
        </div>
        {!table ? <p className="fin-empty">No holdings recorded yet.</p> : (
          <div className="fin-chart-wrap">
            {/* Value is rendered twice and the stylesheet shows one of them: right
                after Ticker under 640px, so a phone sees it without scrolling
                sideways, and in its usual place between Price and Cost basis
                from 640px up. */}
            <table className="fin-nw-table fin-nw-table--holdings">
              <thead>
                <tr>
                  <th>Ticker</th><th className="num fin-nw-narrow-only">Value</th>
                  <th>Name</th><th>Account</th><th className="num">Shares</th><th className="num">Price</th>
                  <th className="num fin-nw-wide-only">Value</th><th className="num">Cost basis</th><th className="num">Gain/Loss</th>
                  <th className="num">%</th><th className="num">Weight</th>
                </tr>
              </thead>
              <tbody>
                {heldRows.map(r => (
                  <tr key={r.id}>
                    <td>{r.isCash ? <>Cash{r.ticker && <div className="fin-nw-sub">{r.ticker}</div>}</> : (r.ticker || '—')}</td>
                    <td className="num fin-nw-narrow-only">{fmtCents(r.value)}</td>
                    <td className="fin-nw-name" title={r.name}>{r.name}</td>
                    <td>{mask(r.mask)}</td>
                    <td className="num">{Number.isFinite(r.quantity) ? r.quantity.toLocaleString(undefined, { maximumFractionDigits: 3 }) : '—'}</td>
                    <td className="num">
                      {fmtCents(r.price)}
                      {r.priceAsOf && <div className="fin-nw-sub">as of {fmtDayShort(r.priceAsOf)}</div>}
                    </td>
                    <td className="num fin-nw-wide-only">{fmtCents(r.value)}</td>
                    <td className="num">{r.costBasis !== null ? fmtCents(r.costBasis) : '—'}</td>
                    <td className="num" style={r.gain !== null ? { color: gainColor(r.gain) } : undefined}>{r.gain !== null ? fmtSigned(r.gain) : '—'}</td>
                    <td className="num" style={r.gainPct !== null ? { color: gainColor(r.gainPct) } : undefined}>{r.gainPct !== null ? fmtPct(r.gainPct) : '—'}</td>
                    <td className="num">{(r.weight * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className="num fin-nw-narrow-only">{fmtCents(heldValue)}</td>
                  <td colSpan="4" />
                  <td className="num fin-nw-wide-only">{fmtCents(heldValue)}</td>
                  <td className="num">{fmtCents(heldBasis)}</td>
                  <td className="num" style={{ color: gainColor(heldGain) }}>{fmtSigned(heldGain)}</td>
                  <td className="num" style={{ color: gainColor(heldGain) }}>{heldBasis > 0 ? fmtPct(heldGain / heldBasis) : '—'}</td>
                  <td className="num">{(heldWeight * 100).toFixed(1)}%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {table && <p className="fin-nw-foot">Weight is the share of all investment holdings. Cost basis and gain/loss totals leave out cash.</p>}
      </div>

      {/* ── Investment activity ── */}
      <div className="fin-section">
        <div className="fin-nw-titlebar">
          <h3 className="fin-section-title">Investment Activity</h3>
          <button style={pill(showAll)} onClick={() => setShowAll(v => !v)}>Show all</button>
        </div>
        {acts.length === 0 ? <p className="fin-empty">No investment activity recorded yet.</p> : (
          <div className="fin-chart-wrap">
            <table className="fin-nw-table fin-nw-table--activity">
              <thead>
                <tr><th>Date</th><th>Account</th><th>Type</th><th>Ticker</th><th className="num">Amount</th></tr>
              </thead>
              <tbody>
                {acts.map(a => (
                  <tr key={a.id}>
                    <td>{fmtDayShort(a.date)}</td>
                    <td>{mask(a.mask)}</td>
                    <td>{KIND_LABEL[a.kind] || a.kind}</td>
                    <td>{a.ticker || '—'}</td>
                    <td className="num">
                      {a.direction === 'in'  && <span style={{ color: '#2e7d32' }}>+{fmtCents(a.amount)} in</span>}
                      {a.direction === 'out' && <span>{fmtCents(a.amount)} out</span>}
                      {a.direction === null  && fmtCents(a.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="fin-nw-foot">
          {showAll
            ? 'Every recorded row, as the brokerage reports it.'
            : 'Last 20. A dividend or interest payment and its same-day reinvestment are one line; sweep-fund moves are hidden.'}
        </p>
      </div>

      {/* ── Manual assets ── */}
      <div className="fin-section">
        <h3 className="fin-section-title">Manual Assets</h3>
        {(assets || []).length === 0 ? <p className="fin-empty">No manual assets recorded yet.</p> : (
          <div className="fin-chart-wrap">
            <table className="fin-nw-table fin-nw-table--assets">
              <thead>
                <tr><th>Asset</th><th>Type</th><th className="num">Quantity</th><th className="num">Value</th><th className="num">Updated</th></tr>
              </thead>
              <tbody>
                {assets.map(a => (
                  <tr key={a.id}>
                    <td>{a.Title}{a.Notes && <div className="fin-nw-sub">{a.Notes}</div>}</td>
                    <td>{a.AssetType || '—'}</td>
                    <td className="num">{Number.isFinite(a.Quantity) ? `${a.Quantity.toLocaleString()}${a.Unit ? ` ${a.Unit}` : ''}` : '—'}</td>
                    <td className="num">
                      {!(a.Value > 0)
                        ? <span className="fin-nw-needs">needs value</span>
                        : fmtBal(a.AssetType === 'Liability' ? -a.Value : a.Value)}
                    </td>
                    <td className="num">{a.Updated ? fmtDay(a.Updated) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {assetInfo.unvalued.length > 0 && (
          <p className="fin-nw-foot">
            {assetInfo.unvalued.length} {assetInfo.unvalued.length === 1 ? 'asset' : 'assets'} not yet valued — left out of net worth until a value is entered.
          </p>
        )}
      </div>

    </div>
  );
}
